import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { getCategories } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { useAuth } from "@/context/AuthContext";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { Heart, Menu, Search, ShoppingBag, ChevronRight, User } from "lucide-react";

const EXTRA_NAV = [
  { to: "/hediye", label: "Hediye" },
  { to: "/shule-club", label: "Shule Club" },
  { to: "/magazalar", label: "Mağazalar" },
  { to: "/bayilik", label: "Bayilik" },
  { to: "/hakkimizda", label: "Hakkımızda" },
];

export function Header() {
  const { cartCount, favorites, setCartOpen } = useStore();
  const { user } = useAuth();
  const [categories, setCategories] = useState([]);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    getCategories().then(setCategories).catch(() => {});
  }, []);

  return (
    <header data-testid="site-header" className="sticky top-0 z-50 glass-header border-b border-shule-line">
      <div className="container-shule relative flex items-center justify-between h-16 lg:h-[76px]">
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild>
            <button data-testid="mobile-menu-button" className="min-w-[44px] min-h-[44px] flex items-center justify-center -ml-2" aria-label="Menüyü aç">
              <Menu className="w-5 h-5" />
            </button>
          </SheetTrigger>
          <SheetContent side="left" className="w-[85vw] max-w-sm bg-shule-paper border-shule-line p-0">
            <SheetTitle className="sr-only">Menü</SheetTitle>
            <div className="flex items-center justify-between px-5 h-16 border-b border-shule-line">
              <Link to="/" onClick={() => setMobileOpen(false)} aria-label="Shule ana sayfa" data-testid="mobile-menu-logo" className="inline-flex">\n                <img src="/assets/img/logo.png" alt="Shule" className="h-9 w-auto" />\n              </Link>
            </div>
            <nav className="px-5 py-6 flex flex-col gap-1 overflow-y-auto">
              <p className="eyebrow mb-3">Kategoriler</p>
              {categories.map((c) => (
                <Link key={c.slug} data-testid={`mobile-nav-${c.slug}`} to={`/kategori/${c.slug}`} onClick={() => setMobileOpen(false)}
                  className="flex items-center justify-between py-3 min-h-[44px] border-b border-shule-line/60 font-serif text-lg">
                  {c.name}
                  <ChevronRight className="w-4 h-4 text-shule-gold" />
                </Link>
              ))}
              <p className="eyebrow mt-6 mb-3">Keşfet</p>
              {EXTRA_NAV.map((n) => (
                <Link key={n.to} data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s/g, "-")}`} to={n.to} onClick={() => setMobileOpen(false)}
                  className="flex items-center justify-between py-3 min-h-[44px] border-b border-shule-line/60 font-serif text-lg">
                  {n.label}
                  <ChevronRight className="w-4 h-4 text-shule-gold" />
                </Link>
              ))}
              <Link data-testid="mobile-nav-account" to={user ? "/hesap" : "/giris"} onClick={() => setMobileOpen(false)}
                className="flex items-center justify-between py-3 min-h-[44px] border-b border-shule-line/60 font-serif text-lg">
                {user ? "Hesabım" : "Giriş Yap"}
                <ChevronRight className="w-4 h-4 text-shule-gold" />
              </Link>
            </nav>
          </SheetContent>
        </Sheet>

        <Link to="/" data-testid="header-logo" className="absolute left-1/2 -translate-x-1/2 select-none">
          <img src="/assets/img/logo.png" alt="Shule — Her Anında Sen" className="h-10 lg:h-12 w-auto" />
        </Link>

        <div className="flex items-center gap-0.5 ml-auto">
          <button data-testid="header-search-button" onClick={() => navigate("/ara")} className="min-w-[44px] min-h-[44px] flex items-center justify-center hover:text-shule-goldhover transition-colors" aria-label="Ara">
            <Search className="w-5 h-5" />
          </button>
          <Link to={user ? "/hesap" : "/giris"} data-testid="header-account-button"
            className="min-w-[44px] min-h-[44px] hidden sm:flex items-center justify-center hover:text-shule-goldhover transition-colors" aria-label="Hesap">
            {user ? (
              <span data-testid="header-account-initial" className="w-7 h-7 rounded-full bg-shule-goldlight border border-shule-gold/40 flex items-center justify-center text-xs font-bold text-shule-coffee">
                {(user.name || user.email || "S")[0].toUpperCase()}
              </span>
            ) : (
              <User className="w-5 h-5" />
            )}
          </Link>
          <Link to="/favoriler" data-testid="header-favorites-button" className="relative min-w-[44px] min-h-[44px] flex items-center justify-center hover:text-shule-goldhover transition-colors" aria-label="Favoriler">
            <Heart className="w-5 h-5" />
            {favorites.length > 0 && (
              <span data-testid="favorites-count-badge" className="absolute top-1 right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-shule-rose text-white text-[10px] font-bold flex items-center justify-center">
                {favorites.length}
              </span>
            )}
          </Link>
          <button data-testid="header-cart-button" onClick={() => setCartOpen(true)} className="relative min-w-[44px] min-h-[44px] flex items-center justify-center hover:text-shule-goldhover transition-colors" aria-label="Sepet">
            <ShoppingBag className="w-5 h-5" />
            {cartCount > 0 && (
              <span data-testid="cart-count-badge" className="absolute top-1 right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-shule-coffee text-shule-paper text-[10px] font-bold flex items-center justify-center">
                {cartCount}
              </span>
            )}
          </button>
        </div>
      </div>

      <nav className="hidden lg:block border-t border-shule-line/60" aria-label="Kategori menüsü">
        <div className="container-shule flex items-center justify-center gap-7">
          <NavLink data-testid="nav-yeni-sezon" to="/koleksiyon/gun-isigi"
            className={({ isActive }) => `text-[13px] font-semibold tracking-wide min-h-[44px] inline-flex items-center transition-colors hover:text-shule-goldhover ${isActive ? "text-shule-goldhover" : "text-shule-mocha"}`}>
            Yeni Sezon
          </NavLink>
          {categories.map((c) => (
            <NavLink key={c.slug} data-testid={`nav-${c.slug}`} to={`/kategori/${c.slug}`}
              className={({ isActive }) => `text-[13px] font-semibold tracking-wide min-h-[44px] inline-flex items-center transition-colors hover:text-shule-goldhover ${isActive ? "text-shule-goldhover" : "text-shule-mocha"}`}>
              {c.name}
            </NavLink>
          ))}
          <NavLink data-testid="nav-hediye" to="/hediye"
            className={({ isActive }) => `text-[13px] font-semibold tracking-wide min-h-[44px] inline-flex items-center transition-colors hover:text-shule-goldhover ${isActive ? "text-shule-goldhover" : "text-shule-mocha"}`}>
            Hediye
          </NavLink>
          <NavLink data-testid="nav-shule-club" to="/shule-club"
            className={({ isActive }) => `text-[13px] font-semibold tracking-wide min-h-[44px] inline-flex items-center transition-colors hover:text-shule-goldhover ${isActive ? "text-shule-goldhover" : "text-shule-mocha"}`}>
            Shule Club
          </NavLink>
        </div>
      </nav>
    </header>
  );
}
